/**
 * API Service Layer
 * Handles all HTTP requests to the backend
 */

const API_BASE = '/api';

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    const errorMessage = data.error?.message || data.error || 'Request failed';
    throw new ApiError(errorMessage, response.status);
  }

  return data;
}

// Raw request helper for multipart uploads (no Content-Type header)
async function requestRaw<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    const errorMessage = data.error?.message || data.error || 'Request failed';
    throw new ApiError(errorMessage, response.status);
  }

  return data;
}

// Auth API
export const authApi = {
  login: (username: string, password: string, studyId: string) =>
    request<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, studyId }),
    }),

  logout: () =>
    request<{ message: string }>('/auth/logout', {
      method: 'POST',
    }),

  me: () =>
    request<{ user: User }>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ message: string; token?: string; user?: User }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

// Users API
export const usersApi = {
  list: () =>
    request<{ users: User[] }>('/users'),

  get: (id: string) =>
    request<{ user: User }>(`/users/${id}`),

  create: (userData: Partial<User> & { password: string }) =>
    request<{ user: User }>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  update: (id: string, userData: Partial<User>) =>
    request<{ user: User }>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    }),

  delete: (id: string) =>
    request<{ message: string }>(`/users/${id}`, {
      method: 'DELETE',
    }),
};

// Studies API
export const studiesApi = {
  // Public endpoint — no auth needed (for login page dropdown)
  listActive: () =>
    request<{ studies: { _id: string; studyCode: string; studyName: string; status: string }[] }>('/studies/active'),

  list: () =>
    request<{ studies: Study[] }>('/studies'),

  get: (code: string) =>
    request<{ study: Study }>(`/studies/${code}`),

  create: (studyData: Partial<Study>) =>
    request<{ study: Study }>('/studies', {
      method: 'POST',
      body: JSON.stringify(studyData),
    }),

  update: (code: string, studyData: Partial<Study>) =>
    request<{ study: Study }>(`/studies/${code}`, {
      method: 'PUT',
      body: JSON.stringify(studyData),
    }),
};

// Subjects API
export const subjectsApi = {
  list: (studyCode: string) =>
    request<{ subjects: Subject[] }>(`/subjects?studyCode=${studyCode}`),

  get: (id: string) =>
    request<{ subject: Subject }>(`/subjects/${id}`),

  create: (subjectData: { studyCode: string; siteId: string }) =>
    request<{ subject: Subject }>('/subjects', {
      method: 'POST',
      body: JSON.stringify(subjectData),
    }),

  update: (id: string, subjectData: Partial<Subject>) =>
    request<{ subject: Subject }>(`/subjects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(subjectData),
    }),
};

// Forms API
export const formsApi = {
  list: (studyCode: string) =>
    request<{ forms: FormTemplate[] }>(`/forms?studyCode=${studyCode}`),

  get: (id: string) =>
    request<{ form: FormTemplate }>(`/forms/${id}`),

  create: (formData: Partial<FormTemplate>) =>
    request<{ form: FormTemplate }>('/forms', {
      method: 'POST',
      body: JSON.stringify(formData),
    }),
};

// Data Entry API
export const dataApi = {
  list: (filters: { studyCode?: string; subjectId?: string; formId?: string }) =>
    request<{ entries: DataEntry[] }>(`/data?${new URLSearchParams(filters as Record<string, string>)}`),

  get: (id: string) =>
    request<{ entry: DataEntry }>(`/data/${id}`),

  create: (entryData: Partial<DataEntry>) =>
    request<{ entry: DataEntry }>('/data', {
      method: 'POST',
      body: JSON.stringify(entryData),
    }),

  update: (id: string, entryData: Partial<DataEntry>) =>
    request<{ entry: DataEntry }>(`/data/${id}`, {
      method: 'PUT',
      body: JSON.stringify(entryData),
    }),

  submit: (id: string) =>
    request<{ entry: DataEntry }>(`/data/${id}/submit`, {
      method: 'POST',
    }),

  sign: (id: string, password: string, meaning: string) =>
    request<{ entry: DataEntry }>(`/data/${id}/sign`, {
      method: 'POST',
      body: JSON.stringify({ password, meaning }),
    }),

  lock: (id: string) =>
    request<{ entry: DataEntry }>(`/data/${id}/lock`, {
      method: 'POST',
    }),

  subjectStatus: (subjectId: string) =>
    request<{
      subject: { subjectId: string; displayId: string; status: string };
      summary: { totalForms: number; missingForms: number; incompleteForms: number; completeForms: number };
      visits: {
        visitId: string;
        visitName: string;
        position: number;
        forms: {
          formId: string;
          formName: string;
          formType: string;
          repeating: boolean;
          status: 'missing' | 'incomplete' | 'complete';
          entries: {
            entryNumber: number;
            entryId: string;
            recordId: string;
            status: string;
            completedFields: number;
            totalFields: number;
            missingRequired: string[];
            isComplete: boolean;
            lastSavedAt: string;
          }[];
          totalFields: number;
          requiredFields: number;
          missingRequired?: string[];
        }[];
      }[];
      eventDriven: {
        formId: string;
        formName: string;
        formType: string;
        repeating: boolean;
        status: 'missing' | 'incomplete' | 'complete';
        entries: {
          entryNumber: number;
          entryId: string;
          recordId: string;
          status: string;
          completedFields: number;
          totalFields: number;
          missingRequired: string[];
          isComplete: boolean;
          lastSavedAt: string;
        }[];
        totalFields: number;
        requiredFields: number;
      }[];
      coreLab: {
        formId: string;
        formName: string;
        formType: string;
        repeating: boolean;
        status: 'missing' | 'incomplete' | 'complete';
        entries: {
          entryNumber: number;
          entryId: string;
          recordId: string;
          status: string;
          completedFields: number;
          totalFields: number;
          missingRequired: string[];
          isComplete: boolean;
          lastSavedAt: string;
        }[];
        totalFields: number;
        requiredFields: number;
      }[];
      cec: {
        formId: string;
        formName: string;
        formType: string;
        repeating: boolean;
        status: 'missing' | 'incomplete' | 'complete';
        entries: {
          entryNumber: number;
          entryId: string;
          recordId: string;
          status: string;
          completedFields: number;
          totalFields: number;
          missingRequired: string[];
          isComplete: boolean;
          lastSavedAt: string;
        }[];
        totalFields: number;
        requiredFields: number;
      }[];
      forms: {
        formId: string;
        formName: string;
        formType: string;
        repeating: boolean;
        status: 'missing' | 'incomplete' | 'complete';
        entries: {
          entryNumber: number;
          entryId: string;
          recordId: string;
          status: string;
          completedFields: number;
          totalFields: number;
          missingRequired: string[];
          isComplete: boolean;
          lastSavedAt: string;
        }[];
        totalFields: number;
        requiredFields: number;
        missingRequired?: string[];
      }[];
    }>(`/data/subject/${subjectId}/status`),

  subjectSummary: () =>
    request<{ subjects: Record<string, {
      dataEntry: { total: number; complete: number; inProgress: number };
      images: { count: number };
      eventDriven: { count: number };
      coreLab: { count: number };
      cec: { count: number };
      queries: { open: number; answered: number };
    }> }>('/data/subject-summary'),
};

// Queries API
export const queriesApi = {
  list: (filters: { studyCode?: string; status?: string }) =>
    request<{ queries: Query[] }>(`/queries?${new URLSearchParams(filters as Record<string, string>)}`),

  get: (id: string) =>
    request<{ query: Query }>(`/queries/${id}`),

  create: (queryData: Partial<Query>) =>
    request<{ query: Query }>('/queries', {
      method: 'POST',
      body: JSON.stringify(queryData),
    }),

  respond: (id: string, response: string) =>
    request<{ query: Query }>(`/queries/${id}/respond`, {
      method: 'POST',
      body: JSON.stringify({ response }),
    }),

  close: (id: string, resolution: string) =>
    request<{ query: Query }>(`/queries/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    }),
};

// Audit API
export const auditApi = {
  list: (filters: { studyCode?: string; recordType?: string; limit?: number }) =>
    request<{ entries: AuditEntry[] }>(`/audit?${new URLSearchParams(filters as Record<string, string>)}`),

  getForRecord: (recordType: string, recordId: string) =>
    request<{ entries: AuditEntry[] }>(`/audit/${recordType}/${recordId}`),
};

// CEC (Clinical Events Committee) API
export const cecApi = {
  // Get all adverse events (across all sites)
  listEvents: (filters: { studyCode?: string; status?: 'pending' | 'adjudicated' }) =>
    request<{ events: CecEvent[] }>(`/cec/events?${new URLSearchParams(filters as Record<string, string>)}`),

  // Get specific adverse event details
  getEvent: (eventId: string) =>
    request<{ event: CecEvent; adjudications: DataEntry[] }>(`/cec/events/${eventId}`),

  // Get available adjudication form templates
  getAdjudicationForms: (studyCode?: string) =>
    request<{ forms: FormTemplate[] }>(`/cec/adjudication-forms${studyCode ? `?studyCode=${studyCode}` : ''}`),

  // Submit adjudication
  adjudicate: (data: { eventId: string; formCode: string; data: Record<string, unknown>; reason?: string }) =>
    request<{ adjudication: DataEntry; message: string }>('/cec/adjudicate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Get subjects with adverse events
  listSubjects: (studyCode?: string) =>
    request<{ subjects: CecSubject[] }>(`/cec/subjects${studyCode ? `?studyCode=${studyCode}` : ''}`),

  // Get CEC workload summary
  getSummary: (studyCode?: string) =>
    request<{ summary: CecSummary }>(`/cec/summary${studyCode ? `?studyCode=${studyCode}` : ''}`),
};

// Files API
export const filesApi = {
  list: (filters: { subjectId?: string; visitId?: string; studyCode?: string }) =>
    request<{ files: FileUpload[] }>(`/files?${new URLSearchParams(filters as Record<string, string>)}`),

  upload: (formData: FormData) =>
    requestRaw<{ file: FileUpload }>('/files/upload', {
      method: 'POST',
      body: formData,
    }),

  // --- Direct browser-to-S3 upload (presigned) — bypasses backend/nginx body limit ---
  presign: (body: { subjectId: string; visitId: string; filename: string }) =>
    request<{ uploadUrl: string; r2Key: string }>('/files/presign', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  register: (body: {
    subjectId: string; visitId: string; visitName?: string; originalName: string;
    mimeType?: string; fileSize?: number; r2Key: string; category?: string; description?: string;
  }) =>
    request<{ file: FileUpload }>('/files/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  uploadToS3: (uploadUrl: string, file: File, onProgress?: (pct: number) => void): Promise<void> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Storage upload failed (HTTP ${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    }),

  // Full presigned flow for one file: get URL -> PUT to S3 -> register metadata
  uploadViaPresign: async (
    file: File,
    meta: { subjectId: string; visitId: string; visitName?: string; category?: string; description?: string },
    onProgress?: (pct: number) => void
  ): Promise<{ file: FileUpload }> => {
    const { uploadUrl, r2Key } = await filesApi.presign({
      subjectId: meta.subjectId, visitId: meta.visitId, filename: file.name,
    });
    await filesApi.uploadToS3(uploadUrl, file, onProgress);
    return filesApi.register({
      subjectId: meta.subjectId,
      visitId: meta.visitId,
      visitName: meta.visitName,
      originalName: file.name,
      mimeType: file.type || 'application/octet-stream',
      fileSize: file.size,
      r2Key,
      category: meta.category,
      description: meta.description,
    });
  },

  getDownloadUrl: (fileId: string) =>
    request<{ url: string; fileName: string }>(`/files/${fileId}/download`),

  delete: (fileId: string) =>
    request<{ message: string }>(`/files/${fileId}`, { method: 'DELETE' }),

  deleteBatch: (fileIds: string[]) =>
    request<{ message: string; count: number }>('/files/batch', {
      method: 'DELETE',
      body: JSON.stringify({ fileIds }),
    }),

  batchDownload: (fileIds: string[]) =>
    request<{ downloads: { fileId: string; url: string; fileName: string }[] }>('/files/batch-download', {
      method: 'POST',
      body: JSON.stringify({ fileIds }),
    }),

  getStats: () =>
    request<{ totalFiles: number; totalSize: number; subjectsWithFiles: number; totalSubjects: number }>('/files/stats'),

  // Direct link (token in query) so the browser shows native download progress
  // while the all-subjects ZIP streams.
  downloadAll: () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token: token || '' });
    const a = document.createElement('a');
    a.href = `${API_BASE}/files/download-all?${params}`;
    a.download = 'all_files.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },

  // Download one subject's files (optionally one visit/context) as a single ZIP.
  // Uses a direct link (token in query) so the browser shows its native download
  // progress while the ZIP streams, instead of buffering a blob silently.
  downloadSubject: (subjectId: string, visitId?: string) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ token: token || '', subjectId });
    if (visitId) params.set('visitId', visitId);
    const a = document.createElement('a');
    a.href = `${API_BASE}/files/download-all?${params}`;
    a.download = 'subject_files.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },
};

// Export API
export const exportApi = {
  downloadFormCsv: async (studyCode: string, formId: string) => {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_BASE}/data/export?studyCode=${studyCode}&formId=${formId}&format=csv`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) {
      const errData = await response.json().catch(() => null);
      throw new ApiError(errData?.error?.message || 'Export failed', response.status);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${studyCode}_${formId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  downloadAllCsv: async (studyCode: string) => {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_BASE}/data/export?studyCode=${studyCode}&format=zip`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new ApiError('Export failed', response.status);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${studyCode}_export.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// Types
/**
 * User type - Per AERO-EDC spec:
 * - 5 roles: admin, site-rc, site, analyst, cec
 * - assignedSites for site-rc and site users
 * - mustChangePassword for first login / password reset
 */
export interface User {
  _id: string;
  id?: string;  // Some endpoints return id instead of _id
  username: string;
  email: string;
  fullName: string;
  role: 'admin' | 'site-rc' | 'site' | 'analyst' | 'cec';
  organization?: string;
  assignedSites?: { _id: string; siteId: string; siteName: string }[];
  isActive: boolean;
  mustChangePassword?: boolean;
  studyId?: string;
  studyCode?: string;
  studyName?: string;
  createdAt?: string;
  lastLogin?: string;
}

export interface Study {
  _id: string;
  studyCode: string;
  studyName: string;
  sponsor: string;
  status: 'setup' | 'active' | 'closed' | 'archived';
  description?: string;
  sites: { siteId: string; siteName: string; isActive: boolean }[];
  createdAt: string;
}

export interface Subject {
  _id: string;
  studyCode?: string;
  study?: string;
  subjectId: string;
  displayId?: string;
  siteId?: string;
  site?: { _id: string; siteId: string; siteName: string } | string;
  status: 'screening' | 'screen-failure' | 'enrolled' | 'active' | 'completed' | 'withdrawn' | 'lost-to-follow-up' | 'deceased';
  screeningDate?: string;
  enrollmentDate?: string;
  createdAt: string;
}

export interface FormTemplate {
  _id: string;
  studyCode: string;
  formId: string;
  formName: string;
  version: string;
  formType: 'site' | 'corelab' | 'cec';
  category?: string;
  description?: string;
  sections: FormSection[];
  signatureRequired: boolean;
  signatureMeaning?: string;
  isActive: boolean;
}

export interface FormSection {
  sectionId: string;
  sectionName: string;
  position: number;
  repeatable: boolean;
  fields: FormField[];
}

export interface FormField {
  fieldId: string;
  label: string;
  type: string;
  options?: { value: string | number; label: string }[];
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
  };
}

export interface DataEntry {
  _id: string;
  studyCode: string;
  subjectId: string;
  formId: string;
  visitId?: string;
  recordId: string;
  status: 'new' | 'in-progress' | 'complete' | 'submitted' | 'signed' | 'locked';
  data: { fieldId: string; value: unknown; repeatIndex?: number }[];
  isLocked: boolean;
  openQueryCount: number;
  completionStatus?: {
    totalFields: number;
    completedFields: number;
    requiredFields: number;
    completedRequired: number;
  };
  signatures?: {
    signerFullName: string;
    signerUsername: string;
    signerRole: string;
    meaning: string;
    signedAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface Query {
  _id: string;
  studyCode: string;
  subjectId: string;
  formId: string;
  fieldId: string;
  queryText: string;
  status: 'open' | 'responded' | 'closed';
  priority: 'low' | 'medium' | 'high';
  responses: { text: string; respondedBy: string; respondedAt: string }[];
  createdAt: string;
}

export interface AuditEntry {
  _id: string;
  timestamp: string;
  userId: string;
  username: string;
  action: string;
  recordType: string;
  recordId: string;
  changes: { field: string; oldValue: unknown; newValue: unknown }[];
}

// CEC Types
export interface CecEvent {
  _id: string;
  subject: { _id: string; subjectId: string; displayId: string };
  site: { _id: string; siteId: string; siteName: string };
  study: { _id: string; studyCode: string; studyName: string };
  formTemplate: { _id: string; formCode: string; formName: string };
  data: Record<string, unknown>;
  createdAt: string;
  adjudicationStatus: 'pending' | 'adjudicated';
  adjudicationForm?: {
    formCode: string;
    formName: string;
    entryId: string;
  };
}

export interface CecSubject {
  _id: string;
  subjectId: string;
  displayId: string;
  site: { _id: string; siteId: string; siteName: string };
  study: { _id: string; studyCode: string; studyName: string };
  aeCount: {
    total: number;
    adjudicated: number;
    pending: number;
  };
}

export interface CecSummary {
  totalAEs: number;
  adjudicatedAEs: number;
  pendingAEs: number;
  adjudicationsByType: Record<string, { formName: string; count: number }>;
}

export interface SourceDoc {
  _id: string;
  docId: string;
  documentType: string;
  documentTypeLabel: string;
  originalName: string;
  fileSize: number;
  fileType: 'pdf' | 'image' | 'zip' | 'other';
  documentDate?: string;
  uploadedAt: string;
  uploadedBy: string | { username: string; fullName: string };
  subject?: { _id: string; subjectId: string; displayId: string };
}

// Source Documents API (clinical source docs for AE entries)
export const sourceDocsApi = {
  list: (filters: { subjectId?: string; documentType?: string; dataEntryId?: string }) =>
    request<{ docs: SourceDoc[] }>(`/source-docs?${new URLSearchParams(filters as Record<string, string>)}`),

  upload: (formData: FormData) =>
    requestRaw<{ doc: SourceDoc }>('/source-docs/upload', {
      method: 'POST',
      body: formData,
    }),

  getDownloadUrl: (docId: string) =>
    request<{ url: string; fileName: string }>(`/source-docs/${docId}/download`),

  delete: (docId: string) =>
    request<{ message: string }>(`/source-docs/${docId}`, { method: 'DELETE' }),

  downloadAll: async (subjectId?: string) => {
    const token = localStorage.getItem('token');
    const a = document.createElement('a');
    const params = new URLSearchParams({ token: token || '' });
    if (subjectId) params.set('subjectId', subjectId);
    a.href = `${API_BASE}/source-docs/download-all?${params}`;
    a.download = 'source_docs.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },
};

export interface FileUpload {
  _id: string;
  fileId: string;
  subject: { _id: string; subjectId: string; displayId: string };
  site: { _id: string; siteId: string; siteName: string };
  visitId: string;
  visitName?: string;
  originalName: string;
  fileType: 'dicom' | 'zip' | 'pdf' | 'image' | 'other';
  mimeType: string;
  fileSize: number;
  category: 'angiography' | 'oct' | 'ivus' | 'tws' | 'other';
  description?: string;
  uploadedBy: { username: string; fullName: string };
  uploadedAt: string;
  createdAt: string;
}

export { ApiError };
